import React, { useState } from 'react';
import { useAppState } from '../../context/AppStateProvider';
import { Briefcase, Plus, Trash2, Edit2, X, Check, ExternalLink, Tag as TagIcon } from 'lucide-react';

const WorkspacePortfolio = () => {
  const { portfolio, addPortfolioItem, updatePortfolioItem, deletePortfolioItem, setSelectedItem } = useAppState();
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({ title: '', url: '', description: '', tags: [], category: '' });
  const [tagInput, setTagInput] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [filterCategory, setFilterCategory] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (formData.title.trim() && formData.url.trim()) {
      if (editingId) {
        await updatePortfolioItem(editingId, formData);
        setEditingId(null);
      } else {
        await addPortfolioItem(
          formData.title.trim(), 
          formData.url.trim(), 
          formData.description.trim(), 
          formData.tags, 
          formData.category.trim()
        );
      }
      setFormData({ title: '', url: '', description: '', tags: [], category: '' });
      setShowForm(false);
    }
  };

  const handleEdit = (item) => {
    setEditingId(item.id);
    setFormData({ 
      title: item.title, 
      url: item.url, 
      description: item.description || '', 
      tags: item.tags || [],
      category: item.category || ''
    });
    setShowForm(true);
  };

  const handleCancel = () => {
    setShowForm(false);
    setEditingId(null);
    setFormData({ title: '', url: '', description: '', tags: [], category: '' });
    setTagInput('');
  };

  const addTag = () => {
    if (tagInput.trim() && !formData.tags.includes(tagInput.trim())) {
      setFormData({ ...formData, tags: [...formData.tags, tagInput.trim()] });
      setTagInput('');
    }
  };

  const removeTag = (tagToRemove) => {
    setFormData({ ...formData, tags: formData.tags.filter(tag => tag !== tagToRemove) });
  };

  // Get unique categories from portfolio
  const categories = [...new Set(portfolio.filter(p => p.category).map(p => p.category))];

  // Filter portfolio items
  const filteredPortfolio = portfolio.filter(item => {
    const matchesSearch = searchQuery === '' || 
      item.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      item.description?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      item.tags?.some(tag => tag.toLowerCase().includes(searchQuery.toLowerCase()));
    
    const matchesCategory = filterCategory === '' || item.category === filterCategory;
    
    return matchesSearch && matchesCategory;
  });

  return (
    <div className="workspace-container" data-testid="workspace-portfolio">
      <div className="workspace-header">
        <div className="workspace-header-left">
          <Briefcase className="workspace-icon" size={24} />
          <div>
            <h2 className="workspace-title">Portfolio</h2>
            <p className="workspace-subtitle">{portfolio.length} items</p>
          </div>
        </div>
        {!showForm && (
          <button 
            className="add-folder-btn"
            data-testid="add-portfolio-btn"
            onClick={() => setShowForm(true)}
          >
            <Plus size={18} />
            Add Item
          </button>
        )}
      </div>

      {/* Search and Filter Bar */}
      <div className="workspace-filters">
        <input
          type="text"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder="Search by title, description, or tags..."
          className="search-input"
          data-testid="portfolio-search"
        />
        {categories.length > 0 && (
          <select
            value={filterCategory}
            onChange={(e) => setFilterCategory(e.target.value)}
            className="filter-select"
            data-testid="portfolio-category-filter"
          >
            <option value="">All Categories</option>
            {categories.map(cat => (
              <option key={cat} value={cat}>{cat}</option>
            ))}
          </select>
        )}
      </div>

      {showForm && (
        <div className="portfolio-form-container" data-testid="portfolio-form">
          <form onSubmit={handleSubmit} className="portfolio-form">
            <div className="form-group">
              <label className="form-label">Title</label>
              <input
                type="text"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                placeholder="Project name"
                className="form-input"
                data-testid="portfolio-title-input"
                autoFocus
                required
              />
            </div>
            <div className="form-group">
              <label className="form-label">URL</label>
              <input
                type="url"
                value={formData.url}
                onChange={(e) => setFormData({ ...formData, url: e.target.value })}
                placeholder="https://example.com"
                className="form-input"
                data-testid="portfolio-url-input"
                required
              />
            </div>
            <div className="form-group">
              <label className="form-label">Description (Optional)</label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Brief description..."
                className="form-textarea"
                data-testid="portfolio-description-input"
                rows="3"
              />
            </div>
            <div className="form-group">
              <label className="form-label">Category (Optional)</label>
              <input
                type="text"
                value={formData.category}
                onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                placeholder="e.g., Web Design, Branding, Mobile App"
                className="form-input"
                data-testid="portfolio-category-input"
                list="portfolio-categories"
                autoComplete="off"
              />
              <datalist id="portfolio-categories">
                {categories.map(cat => (
                  <option key={cat} value={cat} />
                ))}
              </datalist>
              {categories.length > 0 && (
                <div className="form-hint">
                  Existing categories: {categories.join(', ')}
                </div>
              )}
            </div>
            <div className="form-group">
              <label className="form-label">Tags</label>
              <div className="tag-input-container">
                <input
                  type="text"
                  value={tagInput}
                  onChange={(e) => setTagInput(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addTag())}
                  placeholder="Add tag (React, UI/UX, etc.)"
                  className="form-input"
                  data-testid="portfolio-tag-input"
                />
                <button 
                  type="button" 
                  onClick={addTag} 
                  className="tag-add-btn"
                  data-testid="add-tag-btn"
                >
                  Add
                </button>
              </div>
              {formData.tags.length > 0 && (
                <div className="tags-display">
                  {formData.tags.map((tag, index) => (
                    <span key={index} className="tag-badge" data-testid={`tag-${tag}`}>
                      {tag}
                      <button
                        type="button"
                        onClick={() => removeTag(tag)}
                        className="tag-remove"
                        data-testid={`remove-tag-${tag}`}
                      >
                        <X size={12} />
                      </button>
                    </span>
                  ))}
                </div>
              )}
            </div>
            <div className="idea-form-actions">
              <button type="submit" className="save-btn" data-testid="save-portfolio-btn">
                <Check size={16} />
                {editingId ? 'Update' : 'Save'}
              </button>
              <button type="button" onClick={handleCancel} className="cancel-btn" data-testid="cancel-portfolio-btn">
                <X size={16} />
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="portfolio-list">
        {portfolio.length === 0 && !showForm ? (
          <div className="empty-state-large" data-testid="empty-state">
            <Briefcase size={48} className="empty-icon" />
            <p>No portfolio items yet</p>
            <p className="empty-hint">Showcase your best work to prospects</p>
          </div>
        ) : filteredPortfolio.length === 0 ? (
          <div className="empty-state-large">
            <Briefcase size={48} className="empty-icon" />
            <p>No items match your search</p>
            <p className="empty-hint">Try adjusting your filters</p>
          </div>
        ) : (
          <div className="portfolio-grid">
            {filteredPortfolio.map(item => (
              <div 
                key={item.id} 
                className="portfolio-card" 
                data-testid={`portfolio-${item.id}`}
                onClick={() => setSelectedItem(item)}
                style={{ cursor: 'pointer' }}
              >
                <div className="portfolio-card-header">
                  <h3 className="portfolio-card-title">{item.title}</h3>
                  <div className="idea-card-actions">
                    <a
                      href={item.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="idea-action-btn"
                      title="Visit"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <ExternalLink size={14} />
                    </a>
                    <button
                      className="idea-action-btn"
                      data-testid={`edit-portfolio-${item.id}`}
                      onClick={(e) => { e.stopPropagation(); handleEdit(item); }}
                      title="Edit"
                    >
                      <Edit2 size={14} />
                    </button>
                    <button
                      className="idea-action-btn idea-action-delete"
                      data-testid={`delete-portfolio-${item.id}`}
                      onClick={(e) => { e.stopPropagation(); deletePortfolioItem(item.id); }}
                      title="Delete"
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>
                {item.category && (
                  <div className="portfolio-category-badge" data-testid={`portfolio-category-${item.id}`}>
                    {item.category}
                  </div>
                )}
                {item.description && (
                  <p className="portfolio-card-description">{item.description}</p>
                )}
                {item.tags && item.tags.length > 0 && (
                  <div className="portfolio-card-tags">
                    {item.tags.map((tag, index) => (
                      <span key={index} className="portfolio-tag">{tag}</span>
                    ))}
                  </div>
                )}
                <div className="portfolio-card-footer">
                  <span className="portfolio-url">{new URL(item.url).hostname}</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default WorkspacePortfolio;
